/* eslint-disable react/no-array-index-key */
import {red} from "@ant-design/colors";
import {CloseOutlined, DeleteOutlined, EditOutlined, FilePdfOutlined, InfoCircleOutlined, InfoCircleTwoTone} from "@ant-design/icons";
import {Alert, Button, Checkbox, Input, message as antMessage, Modal, Space, Spin, Tag, Tooltip, Switch, message} from "antd";
import {ApolloError} from "@apollo/client";
import classnames from "classnames";
import React, {FC, memo, useEffect, useState} from "react";
import uuid from "uuid";
import {SentMailsResult} from "../../../../server/mailjet.types";
import {renderTitle} from "../../../../shared/templates/handleTemplateVariables";
import {
    EditBookingInput,
    GetMjmlTemplateByIdQuery,
    GetTemplateByIdQuery,
    NewBookingInput,
    Template,
    TemplateType,
    useGetMjmlTemplateByIdQuery,
    useGetTemplateByIdQuery,
    useGetTemplateByTypeQuery,
    useUpdateTemplateMutation,
    useSendEmailTemplateMutation,
    SendTemplateInput,
    MailjetEmailTemplates,
    SendParamsMessageInput,
    EmailData,
    MailDataType,
    useDeleteTemplateMutation,
    SubscriptionType,
    useGetCompanyInfoQuery,
    GetCompanyInfoQuery,
    useGetOwnerEmailQuery,
    GetOwnerEmailQuery,
    useGetReceiversByCourseIdsQuery,
    GetReceiversByCourseIdsQuery,
    GetTemplatesDocument,
    useSetIncludeCourseDetailsMutation,
    GetTemplateByIdDocument,
    useRenderTemplateForLetterPdfLazyQuery,
    RenderTemplateForLetterPdfInput,
    useGetCompanySettingsQuery,
} from "../../../generated/graphql";
import i18n from "../../../services/i18n";
import MjmlLoader from "../../../views/MjmlPreview/MjmlLoader/MjmlLoader";
import {useTextBlockPreview} from "../../../views/MjmlPreview/ResolvedPreview/useTextBlockPreview";
import ResolvedPreviewBar from "../../../views/MjmlPreview/ResolvedPreview/ResolvedPreviewBar";
import {GET_TEMPLATES} from "../templates.graphql";
import TemplateInputField from "../shared/TemplateInputField";
import css from "./MailTemplateEditor.less";
import DraggableModal from "../../../views/DraggableModal/DraggableModal";
import LetterPdfPreviewModal from "../EmailRefactoring/letterPdf/LetterPdfPreviewModal";
import {LetterPdfData} from "../EmailRefactoring/letterPdf/generateLetterPdf";

export type UseCase = "EDITOR" | "PREVIEW" | "SENDMAIL" | "SELECT";

const defaultFieldContent: GetTemplateByIdQuery["template"] = {
    subject: "subject",
    headerText: "headerText",
    footerText: "footerText",
    signature: "signature",
    id: "",
    name: "",
    templateType: TemplateType.FreeText,
    isDefault: false,
    includeCourseDetails: false,
};

type AttachmentStatus = {
    enabled: boolean;
    reason: string;
};

// Typ für editedTemplateInput (nur die Text-Felder die bearbeitet werden können)
type EditedTemplateInputType = {
    subject?: string;
    headerText?: string;
    footerText?: string;
    signature?: string;
};

// Zusätzliche Aktionen, die beim Absenden mit der Bestätigungs-Mail ausgelöst werden
// können (z.B. Mollie-Zahlungslink-Dispatch bei Admin-Buchungen).
export type ConfirmActionExtras = {
    sendPaymentLink?: boolean;
};

type MailTemplateEditorProps = {
    style?: React.CSSProperties;
    data?: NewBookingInput | EditBookingInput;
    bookingId?: string;
    open?: boolean;
    setVisibility?: (e: boolean) => void;
    dbTemplateId?: string;
    useCase?: UseCase;
    templateType?: TemplateType;
    confirmAction?: (e: boolean, editedTemplateInput?: EditedTemplateInputType, extras?: ConfirmActionExtras) => void;
    asModal?: boolean;
    liftSelectedTemplate?: (dbTemplateId: string, templateType: TemplateType) => void;
    switchEditMode?: "EDIT" | "PREVIEW";
    emailData?: EmailData;
    mailDataType?: MailDataType;
    receivers: Array<string>;
    showInvoiceAttachments?: boolean;
    invoiceAttachmentStatus?: AttachmentStatus;
    reversalInvoiceAttachmentStatus?: AttachmentStatus;
    /** Blendet den "Nicht senden" Button aus (z.B. für nachträgliches Versenden aus dem Menü) */
    hideNotSendButton?: boolean;
    /** Zeigt eine zusätzliche Checkbox für "Zahlungslink per E-Mail senden" (nur Admin-Buchung mit Online-Zahlung). */
    showPaymentLinkCheckbox?: boolean;
    /** Default-Zustand der Zahlungslink-Checkbox (abhängig von Org-Setting sendPaymentLinkOnAdminBooking). */
    paymentLinkCheckboxDefault?: boolean;
    /** Kontext-Label zum Zahlungslink, z.B. "Gesamtbetrag: 320 €" oder "Erste Rate: 80 €". */
    paymentLinkAmountLabel?: string;
    /**
     * Ueberschreibt das Label/Tooltip der Zahlungs-Checkbox (z.B. "Rechnung und
     * Zahlungsinfos senden" bei Mollie-Ueberweisung statt "Zahlungslink senden").
     * Der generische `extras.sendPaymentLink`-Boolean bleibt unveraendert.
     */
    paymentCheckboxLabelOverride?: string;
    /**
     * Hinweistext (banktransfer), der gezeigt wird, wenn der Versand-Haken
     * abgewaehlt oder der Dialog mit abgewaehltem Haken geschlossen wird — der
     * Kunde erhaelt sonst keine Bankdaten. Nur gesetzt im Ueberweisungs-Fall.
     */
    paymentCheckboxOffWarning?: string;
};

const MailTemplateEditor: FC<MailTemplateEditorProps> = ({
    style,
    data,
    bookingId,
    open,
    setVisibility = () => console.log("setVisibility not set"),
    dbTemplateId,
    useCase = "PREVIEW",
    templateType,
    confirmAction,
    asModal = false,
    liftSelectedTemplate,
    switchEditMode = "PREVIEW",
    emailData,
    mailDataType,
    receivers,
    showInvoiceAttachments = false,
    invoiceAttachmentStatus,
    reversalInvoiceAttachmentStatus,
    hideNotSendButton = false,
    showPaymentLinkCheckbox = false,
    paymentLinkCheckboxDefault = false,
    paymentLinkAmountLabel,
    paymentCheckboxLabelOverride,
    paymentCheckboxOffWarning,
}) => {
    const [edit, setEdit] = useState<boolean>(switchEditMode === "EDIT");
    const [templateContent, setTemplateContent] = useState<GetTemplateByIdQuery["template"]>(defaultFieldContent);
    const [templateContentBeforeEdit, setTemplateBeforeEdit] = useState<GetTemplateByIdQuery["template"]>(defaultFieldContent);
    const [actualDbTemplateId, setActualDbTemplateId] = useState<string>();
    const [templateHasBeenEdited, setTemplateHasBeenEdited] = useState<boolean>(false);
    const [emailToSendCount, setEmailToSendCount] = useState<number>();
    const [attachInvoices, setAttachInvoices] = useState<boolean>(false);
    const [attachReversalInvoices, setAttachReversalInvoices] = useState<boolean>(false);
    const [sendPaymentLinkEnabled, setSendPaymentLinkEnabled] = useState<boolean>(paymentLinkCheckboxDefault);

    useEffect(() => {
        setSendPaymentLinkEnabled(paymentLinkCheckboxDefault);
    }, [paymentLinkCheckboxDefault]);
    const [letterPdfModalVisible, setLetterPdfModalVisible] = useState(false);
    const [letterData, setLetterData] = useState<LetterPdfData | null>(null);

    // CHECK IF IS DEMO ACCOUNT AND REPLACE EMAIL ADRESSES ----------------------------------------------------------

    const {data: companyInfoData} = useGetCompanyInfoQuery();

    const companyInfo: GetCompanyInfoQuery["companyInfo"] | undefined = companyInfoData?.companyInfo;

    const subscriptionType = SubscriptionType[companyInfo?.subscriptionType ?? SubscriptionType.TestSubscription];
    // const {data: companyInfoData} = useGetCompanyInfoQuery();

    // CHECK IF FEATURE LESSON BOOKING IS ENABLED
    const featureLessonBooking = companyInfoData?.companyInfo?.subscription?.featureLessonBooking;

    useEffect(() => {
        setEdit(switchEditMode === "EDIT");
    }, [switchEditMode]);

    // Reset templateHasBeenEdited when modal is opened/closed
    useEffect(() => {
        if (!open) {
            setTemplateHasBeenEdited(false);
        }
    }, [open]);

    // Fetch Template from DB ------------------------------------------------------------------------------------------------------------------------------

    // There are two ways to fetch the template from the DB:
    // 1. Fetch the template by ID // is preferred
    // 2. Fetch the template by templateType ==> is used for cases like bookingCreation, where the templateType is not yet known

    const {
        data: dbTemplateByTypeData,
        loading: dbLoadingTemplateByType,
        error: dbErrorTemplateByType,
    } = useGetTemplateByTypeQuery({
        skip: Boolean((!templateType || dbTemplateId) ?? templateType === TemplateType.FreeText),
        variables: {
            templateType: templateType ?? TemplateType.FreeText,
        },
        fetchPolicy: "network-only",
    });

    const dbTemplateByType = dbTemplateByTypeData?.templateByType;

    const {
        data: dbTemplateByIdData,
        loading: dbLoadingTemplateById,
        error: dbErrorTemplateById,
        refetch: refetchDbTemplateById,
    } = useGetTemplateByIdQuery({
        skip: Boolean(!dbTemplateId),
        variables: {id: dbTemplateId ?? actualDbTemplateId ?? ""},
        fetchPolicy: "network-only",
    });

    const dbTemplateById = dbTemplateByIdData?.template;

    useEffect(() => {
        if (dbTemplateById) {
            setActualDbTemplateId(dbTemplateById.id);
            setTemplateContent(dbTemplateById);
            setTemplateBeforeEdit(dbTemplateById);
            setTemplateHasBeenEdited(false); // Reset edit state when template is loaded
        } else if (dbTemplateByType) {
            setActualDbTemplateId(dbTemplateByType.id);
            setTemplateContent(dbTemplateByType);
            setTemplateBeforeEdit(dbTemplateByType);
            setTemplateHasBeenEdited(false); // Reset edit state when template is loaded
        }
        // OOS-1 (UAT 2026-05-17): Frueher stand hier `return () => console.log("template not found")` —
        // ein sinnloses Cleanup-Funktion-Return das den useEffect-Contract verletzt und nur Log-Noise
        // produziert. Wenn kein Template gefunden wird, wird das via `noTemplateLoaded` weiter unten
        // im UI angezeigt (Send-Button disabled + Tooltip-Erklaerung).
    }, [dbTemplateById, dbTemplateByType]);

    // OOS-1 (UAT 2026-05-17): Wenn weder dbTemplateById noch dbTemplateByType ein Template
    // geliefert haben (Loading abgeschlossen + beide null), kann der Send-Flow nicht
    // funktionieren — Render-Pipeline laeuft ins Leere, keine Mail wird versendet, keine
    // Versandhistorie. Vorher: Senden-Button war aktiv und Klick fuehrte zu silent failure.
    // Jetzt: Button wird disabled + Tooltip erklaert das Problem.
    const dbTemplateByTypeFinished = !dbLoadingTemplateByType && !dbErrorTemplateByType;
    const dbTemplateByIdFinished = !dbLoadingTemplateById && !dbErrorTemplateById;
    const noTemplateLoaded =
        templateType !== TemplateType.FreeText && templateType !== TemplateType.NoTemplate && dbTemplateByTypeFinished && dbTemplateByIdFinished && !dbTemplateById && !dbTemplateByType;

    // Fetch Mjml from mjml ------------------------------------------------------------------------------------------------------------------------------

    const renderWithoutCourseDetails = dbTemplateById?.includeCourseDetails === false || dbTemplateByType?.includeCourseDetails === false;

    // 4250298 blankMailNewSyntax
    const mailjetTemplateId =
        templateType === TemplateType.FreeText || templateType === TemplateType.NoTemplate
            ? "4250298"
            : renderWithoutCourseDetails
            ? "5879362" // Template where the CourseDetails are not included
            : "2046014";
    //   const mailjetTemplateId =
    //       templateType === TemplateType.FreeText ? "2288419" : "2046014";

    const {
        data: mjmlTemplateData,
        loading: mjmlTemplateLoading,
        error: mjmlTemplateError,
    } = useGetMjmlTemplateByIdQuery({
        variables: {templateId: mailjetTemplateId},
    });

    const mjmlTemplate: GetMjmlTemplateByIdQuery["getEmailTemplateById"] | undefined = mjmlTemplateData?.getEmailTemplateById;

    const [createUpdateMutation, {loading: updateLoading, error: createUpdateError}] = useUpdateTemplateMutation();

    const [deleteTemplate, {loading: deleting, error: errorDeleting}] = useDeleteTemplateMutation();

    // Brief-PDF Lazy Query ---------------------------------------------------------------------------------------------------------------

    // #514: Absenderland für das landesübliche Steuer-ID-Label im Brief-Footer —
    // der Render-Payload transportiert das Land nicht; gleiche Ableitung wie in
    // EmailFooter.tsx (gecachte Company-Settings-Query).
    const {data: companySettingsData} = useGetCompanySettingsQuery();
    const senderCountry = companySettingsData?.companySettings?.ownerCountry ?? undefined;

    const [renderTemplateForLetterPdf, {loading: renderLetterPdfLoading}] = useRenderTemplateForLetterPdfLazyQuery({
        onCompleted: (queryData) => {
            if (queryData?.renderTemplateForLetterPdf) {
                const rendered = queryData.renderTemplateForLetterPdf;
                setLetterData({
                    senderCountry,
                    subject: rendered.subject,
                    headerText: rendered.headerText,
                    footerText: rendered.footerText,
                    signature: rendered.signature,
                    recipientFirstName: rendered.recipientFirstName || undefined,
                    recipientLastName: rendered.recipientLastName || undefined,
                    recipientStreet: rendered.recipientStreet || undefined,
                    recipientHouseNumber: rendered.recipientHouseNumber || undefined,
                    recipientPostCode: rendered.recipientPostCode || undefined,
                    recipientCity: rendered.recipientCity || undefined,
                    recipientAddition: rendered.recipientAddition || undefined,
                    senderName: rendered.senderName || undefined,
                    senderStreet: rendered.senderStreet || undefined,
                    senderStreetNumber: rendered.senderStreetNumber || undefined,
                    senderZip: rendered.senderZip || undefined,
                    senderCity: rendered.senderCity || undefined,
                    senderLogoBase64: rendered.senderLogoBase64 || undefined,
                    senderPhone: rendered.senderPhone || undefined,
                    senderEmail: rendered.senderEmail || undefined,
                    senderWebsite: rendered.senderWebsite || undefined,
                    senderBankName: rendered.senderBankName || undefined,
                    senderIban: rendered.senderIban || undefined,
                    senderBic: rendered.senderBic || undefined,
                    senderTaxId: rendered.senderTaxId || undefined,
                    senderTaxNumber: rendered.senderTaxNumber || undefined,
                    courseDetails: rendered.courseDetails
                        ? {
                              courseName: rendered.courseDetails.courseName || undefined,
                              courseNumber: rendered.courseDetails.courseNumber || undefined,
                              locationName: rendered.courseDetails.locationName || undefined,
                              locationStreet: rendered.courseDetails.locationStreet || undefined,
                              locationCity: rendered.courseDetails.locationCity || undefined,
                              courseStartDate: rendered.courseDetails.courseStartDate || undefined,
                              courseEndDate: rendered.courseDetails.courseEndDate || undefined,
                              courseTime: rendered.courseDetails.courseTime || undefined,
                              coursePrice: rendered.courseDetails.coursePrice || undefined,
                              courseLessonsCount: rendered.courseDetails.courseLessonsCount || undefined,
                              attendeeName: rendered.courseDetails.attendeeName || undefined,
                          }
                        : undefined,
                });
                setLetterPdfModalVisible(true);
            }
        },
        fetchPolicy: "network-only",
    });

    // Methods ------------------------------------------------------------------------------------------------------------------------------

    const updateTemplate = async () => {
        console.log("updateTemplate");
        if (!templateContent) {
            return null;
        }
        try {
            const {headerText, subject, signature, footerText, name} = templateContent;

            await createUpdateMutation({
                variables: {
                    id: dbTemplateId ?? actualDbTemplateId ?? "",
                    editTemplateData: {
                        headerText,
                        subject,
                        signature,
                        footerText,
                        name,
                    },
                },
                refetchQueries: ["GetTemplatesQuery", {query: GetTemplatesDocument}],
            });

            if (templateContent === templateContentBeforeEdit) {
                // antMessage.info("Es wurde nichts geändert");
            } else {
                antMessage.success("Vorlage gespeichert");
            }
        } catch (e) {
            console.log("updateTemplate error: ", e);
            throw new Error(`Error on updating templateData: ${(e as Error)?.message}`);
        } finally {
            refetchDbTemplateById();
        }
    };

    const resetFields = () => {
        setTemplateContent(templateContentBeforeEdit);
        setEdit(false);
    };

    const handleOk = () => {
        updateTemplate();
        resetFields();
        setVisibility(false);
    };

    const handleCancel = () => {
        resetFields();
        setVisibility(false);
    };

    const handleTextChange = (description: TemplateField, content: string) => {
        setTemplateHasBeenEdited(true);
        setTemplateContent({...templateContent, [description]: content});
    };

    const handleTemplateSelection = () => {
        if (liftSelectedTemplate && templateType) {
            liftSelectedTemplate(actualDbTemplateId ?? "", templateType);
            resetFields();
            setVisibility(false);
        }
    };

    const handleDeleteTemplate = async () => {
        Modal.confirm({
            content: `Vorlage ${templateContent.name} dauerhaft löschen?`,
            okText: "Entgültig löschen",
            okButtonProps: {
                danger: true,
                loading: deleting,
            },
            okType: "danger",
            style: {
                display: "flex",
                justifyContent: "space-evenly",
                textAlign: "center",
                fontWeight: 600,
                fontSize: "1.2rem",
            },
            icon: null,
            onOk: () => {
                try {
                    deleteTemplate({
                        variables: {id: templateContent.id},
                        refetchQueries: [{query: GET_TEMPLATES}],
                    }).then(() => {
                        antMessage.success("Vorlage gelöscht");
                        setVisibility(false);
                    });
                } catch (error) {
                    console.log("Error deleting template: ", error);
                }
            },
        });
    };

    // Errorview ------------------------------------------------------------------------------------------------------------------------------

    const ErrorView = ({messages}: {messages: Array<ApolloError | undefined>}) => {
        return (
            <>
                {messages.map((message) => {
                    if (message) {
                        console.log("message: ", message);

                        return <div key={message?.extraInfo}>{message.message}</div>;
                    }

                    return null;
                })}
            </>
        );
    };

    // Fields --------------------------------------------------------------------------------------------------------------------------------

    type TemplateField = Extract<keyof GetTemplateByIdQuery["template"], string>;

    const textFields = ["subject", "headerText"];

    if (templateType !== TemplateType.NoTemplate) {
        textFields.push("footerText", "signature");
    }

    const nameInputField = templateType === TemplateType.FreeText && (
        // return (
        <div key="name" style={{margin: 20}}>
            <h4>{i18n.containers.templates.EmailTemplates.sections.name()}</h4>
            <Input value={templateContent.name ?? undefined} onChange={(e) => handleTextChange("name" as TemplateField, e.target.value)} />
        </div>
        // );
        // }
    );

    // Modal title --------------------------------------------------------------------------------------------------------------------------------

    const headerSection = (dbTemplate: Pick<Template, "id" | "templateType" | "name" | "headerText" | "footerText" | "signature" | "subject"> | undefined, useCase: UseCase) => {
        if (dbTemplate?.templateType) {
            const btnLeftType = edit ? "default" : "primary";
            const btnRightType = edit ? "primary" : "default";

            const title = dbTemplate.templateType === TemplateType.FreeText ? dbTemplate.name ?? "" : renderTitle(dbTemplate.templateType as TemplateType);

            if (useCase === "SELECT") {
                return (
                    <header className={classnames(css.header)}>
                        <h2>{renderTitle(title)}</h2>
                    </header>
                );
            }

            return (
                <header className={classnames(css.header)}>
                    <h2>{renderTitle(title)}</h2>
                    <div className={classnames(css.btn_container)}>
                        {templateContent.templateType === TemplateType.FreeText && (
                            <Button
                                // className={classnames(css.btn_prim)}
                                style={{
                                    marginRight: 10,
                                    color: red[4],
                                    border: "none",
                                }}
                                type="link"
                                danger
                                icon={<DeleteOutlined />}
                                // type={btnLeftType}
                                // onClick={() => setEdit(false)}
                                onClick={handleDeleteTemplate}
                            >
                                {i18n.containers.templates.EmailTemplates.modalButtons.delete()}
                            </Button>
                        )}
                        <Button className={classnames(css.btn_prim)} type={btnLeftType} onClick={() => setEdit(false)}>
                            {i18n.containers.templates.EmailTemplates.modalButtons.preview()}
                        </Button>

                        <Button
                            className={classnames(css.btn_def)}
                            type={btnRightType}
                            // onClick={handleEdit}
                            onClick={() => setEdit(true)}
                        >
                            {i18n.containers.templates.EmailTemplates.modalButtons.adjust()}
                        </Button>
                    </div>
                </header>
            );
        }

        return <div>{"Error on getting Title"}</div>;
    };

    // Modal footer --------------------------------------------------------------------------------------------------------------------------------

    // Senden ohne Speichern: Übergibt die bearbeiteten Template-Texte an confirmAction
    const shouldSendMail = (confirmed: boolean, withEditedTemplate?: boolean) => {
        if (confirmAction) {
            // Zahlungslink-Checkbox wird unabhängig vom "Senden"/"Nicht senden"-Button respektiert:
            // Admin kann Bestätigung überspringen, Zahlungslink trotzdem senden (oder umgekehrt).
            const extras: ConfirmActionExtras | undefined = showPaymentLinkCheckbox ? {sendPaymentLink: sendPaymentLinkEnabled} : undefined;

            if (confirmed && withEditedTemplate && templateHasBeenEdited) {
                // Bearbeitete Texte übergeben (Senden ohne Speichern)
                const editedTemplateInput: EditedTemplateInputType = {
                    subject: templateContent.subject ?? undefined,
                    headerText: templateContent.headerText ?? undefined,
                    footerText: templateContent.footerText ?? undefined,
                    signature: templateContent.signature ?? undefined,
                };

                confirmAction(confirmed, editedTemplateInput, extras);
            } else {
                // Ohne bearbeitete Texte senden (Template aus DB verwenden)
                confirmAction(confirmed, undefined, extras);
            }
        }
    };

    // Schließen/Abbrechen (X, Esc, Maskenklick) darf einen aktiven Versand-Haken
    // (Zahlungslink/Rechnung) nicht still verwerfen: bei sichtbarem Haken läuft der
    // Dismiss über shouldSendMail(false), das die extras durchreicht — eine angehakte
    // Rechnung geht also auch beim Schließen noch raus. Bei abgewähltem Haken im
    // banktransfer-Fall zeigen wir zusätzlich den Kunden-Hinweis. Für alle anderen
    // Fälle (keine Checkbox bzw. kein confirmAction) bleibt das bisherige Verhalten.
    const dismissWithoutDiscardingSend = (fallback: () => void) => {
        if (showPaymentLinkCheckbox && confirmAction) {
            if (!sendPaymentLinkEnabled && paymentCheckboxOffWarning) {
                antMessage.warning(paymentCheckboxOffWarning);
            }
            shouldSendMail(false);
            return;
        }
        fallback();
    };

    const [setIncludeCourseDetails] = useSetIncludeCourseDetailsMutation({
        refetchQueries: ["GetTemplateById", {query: GetTemplateByIdDocument, variables: {id: dbTemplateId}}],
    });

    const IncludeCourseDetails = ({editMode, style, template}: {editMode: boolean; style?: React.CSSProperties; template?: GetTemplateByIdQuery["template"]}) => {
        if (!template) {
            console.log("===> !template");

            return null;
        }

        const {id, isDefault, includeCourseDetails} = template;

        if (!id || !isDefault) {
            return null;
        }

        const includeCourseDetailsText = includeCourseDetails
            ? i18n.containers.templates.EmailTemplates.includeCourseDetails.included()
            : i18n.containers.templates.EmailTemplates.includeCourseDetails.notIncluded();

        const setCourseDetails = async (show: boolean) => {
            try {
                await setIncludeCourseDetails({
                    variables: {
                        templateId: id,
                        includeCourseDetails: show,
                    },
                });
            } catch (error) {
                console.log("===> error", error);
            }
        };

        const description = editMode
            ? i18n.containers.templates.EmailTemplates.includeCourseDetails.descriptionWithHint()
            : i18n.containers.templates.EmailTemplates.includeCourseDetails.description();

        return (
            <Space style={{...style}}>
                <Switch onChange={async (e) => setCourseDetails(e)} checked={includeCourseDetails} disabled={editMode} />
                <span>{includeCourseDetailsText}</span>
                <Tooltip title={description}>
                    <InfoCircleTwoTone />
                </Tooltip>
            </Space>
        );
    };

    /**
     * Laedt die gerenderten Template-Daten fuer die Brief-PDF-Vorschau.
     * Bestimmt die richtige Entity-ID basierend auf Props oder emailData.
     */
    const handleLetterPdfClick = () => {
        const tplId = actualDbTemplateId || dbTemplateId;
        if (!tplId) return;

        const input: RenderTemplateForLetterPdfInput = {
            templateId: tplId,
        };

        // Booking-ID bestimmen: direkt aus Props oder aus emailData.ids
        if (bookingId) {
            input.bookingId = bookingId;
        } else if (emailData?.ids && emailData.ids.length > 0) {
            // emailData.ids enthaelt die IDs der selektierten Entities
            const firstId = emailData.ids[0];
            if (emailData.mailDataType === MailDataType.Booking) {
                input.bookingId = firstId;
            } else if (emailData.mailDataType === MailDataType.Booker) {
                input.bookerId = firstId;
            } else if (emailData.mailDataType === MailDataType.Attendee) {
                input.attendeeId = firstId;
            } else if (emailData.mailDataType === MailDataType.ScheduledPayment) {
                input.scheduledPaymentId = firstId;
            }
        }

        // Bearbeitete Texte mitschicken wenn Template bearbeitet wurde
        if (templateHasBeenEdited && templateContent) {
            input.editedTemplateInput = {
                subject: templateContent.subject ?? undefined,
                headerText: templateContent.headerText ?? undefined,
                footerText: templateContent.footerText ?? undefined,
                signature: templateContent.signature ?? undefined,
            };
        }

        renderTemplateForLetterPdf({variables: {input}});
    };

    const Footer = ({useCase}: {useCase: UseCase}) => {
        if (useCase === "PREVIEW") {
            // useCase === "PREVIEW"---------------------------------------------------------------
            console.log("footer PREVIEW");

            return (
                <Space>
                    <>
                        {templateType === TemplateType.SettlementConfirmation && (
                            <Tooltip title="Feature befindet sich in der Entwicklung :)">
                                <Button key="invoice" disabled>
                                    {"PDF-Rechnung"}
                                </Button>
                            </Tooltip>
                        )}
                    </>
                    <Button key="cancel" onClick={() => shouldSendMail(false)} danger ghost>
                        {i18n.containers.templates.EmailTemplates.modalButtons.doNotSend()}
                    </Button>
                    <Button key="ok" onClick={() => shouldSendMail(true)} loading={updateLoading || sendTemplateMailLoading} type="primary">
                        {i18n.containers.templates.EmailTemplates.modalButtons.selectTemplate()}
                    </Button>
                </Space>
            );
        } else if (useCase === "EDITOR") {
            // useCase === "EDITOR"---------------------------------------------------------------
            // console.log("footer EDITOR");

            return (
                <div
                    style={{
                        width: "100%",
                        display: "flex",
                    }}
                >
                    <IncludeCourseDetails editMode={edit} style={{marginRight: "auto"}} template={dbTemplateById} />
                    <Space>
                        <Button key="back" onClick={handleCancel} danger ghost>
                            {i18n.containers.templates.EmailTemplates.modalButtons.cancel()}
                        </Button>
                        <Button key="submit" onClick={handleOk} type="primary">
                            {i18n.containers.templates.EmailTemplates.modalButtons.save()}
                        </Button>
                    </Space>
                </div>
            );
        } else if (useCase === "SELECT") {
            // useCase === "SELECT"--------------------------------------------------------------------
            console.log("footer SELECT");

            if (templateType === TemplateType.NoTemplate) {
                handleTemplateSelection();
            }

            return (
                <>
                    <Button key="back" onClick={handleCancel} danger ghost>
                        {i18n.containers.templates.EmailTemplates.modalButtons.cancel()}
                    </Button>
                    <Button key="submit" onClick={handleTemplateSelection} type="primary">
                        {i18n.containers.templates.EmailTemplates.modalButtons.selectTemplate()}
                    </Button>
                </>
            );
        }
        console.log("footer SENDMAIL");

        const noReceiverFound = (receivers.length < 1 || receivers[0] === "") && mailDataType !== MailDataType.Course;

        // Invoice Checkbox Logic (same as in EmailFooter.tsx)
        const InvoiceCheckbox = () => {
            if (!showInvoiceAttachments) {
                return null;
            }

            // Priorität: Wenn Storno-Rechnungen verfügbar sind, zeige diese, sonst normale Rechnungen
            const showReversalInvoice = reversalInvoiceAttachmentStatus?.enabled;

            if (showReversalInvoice) {
                const tooltipTitle = "Hängt die Storno-Rechnung als PDF-Datei an die Email an. Verfügbar wenn alle Buchungen storniert sind und eine Storno-Rechnung haben.";

                return (
                    <Tooltip title={tooltipTitle}>
                        <Space size="small">
                            <FilePdfOutlined style={{color: "#1890ff", marginRight: 5}} />
                            <Checkbox style={{fontWeight: "bold"}} checked={attachReversalInvoices} onChange={(e) => setAttachReversalInvoices(e.target.checked)}>
                                {"Storno-Rechnung anhängen"}
                            </Checkbox>
                        </Space>
                    </Tooltip>
                );
            }

            // Zeige normale Rechnung (aktiviert oder deaktiviert)
            const tooltipTitle = invoiceAttachmentStatus?.enabled
                ? "Hängt die Rechnung als PDF-Datei an die Email an. Verfügbar wenn alle Buchungen abgerechnet und nicht storniert sind."
                : "Verfügbar wenn alle Buchungen abgerechnet und nicht storniert sind.";

            return (
                <Tooltip title={tooltipTitle}>
                    <Space size="small">
                        <Checkbox style={{fontWeight: "bold"}} checked={attachInvoices} disabled={!invoiceAttachmentStatus?.enabled} onChange={(e) => setAttachInvoices(e.target.checked)}>
                            <FilePdfOutlined style={{color: invoiceAttachmentStatus?.enabled ? "#1890ff" : "#d9d9d9", marginRight: 5}} />
                            {"Rechnung anhängen"}
                        </Checkbox>
                    </Space>
                </Tooltip>
            );
        };

        // Zahlungslink-Checkbox: nur bei Admin-Booking-Create mit Online-Zahlung (Mollie) sichtbar.
        const PaymentLinkCheckbox = () => {
            if (!showPaymentLinkCheckbox) {
                return null;
            }

            const checkboxLabel = paymentCheckboxLabelOverride ?? i18n.containers.bookings.BookingCreateEdit.mailEditor.sendPaymentLinkLabel();

            const tooltipTitle = paymentLinkAmountLabel ? `${checkboxLabel} – ${paymentLinkAmountLabel}` : checkboxLabel;

            return (
                <Tooltip title={tooltipTitle}>
                    <Space size="small">
                        <Checkbox
                            style={{fontWeight: "bold", whiteSpace: "nowrap"}}
                            checked={sendPaymentLinkEnabled}
                            onChange={(e) => {
                                setSendPaymentLinkEnabled(e.target.checked);
                                // Abwählen im banktransfer-Fall: Kunde erhält sonst keine Bankdaten.
                                if (!e.target.checked && paymentCheckboxOffWarning) {
                                    antMessage.warning(paymentCheckboxOffWarning);
                                }
                            }}
                        >
                            {checkboxLabel}
                            {paymentLinkAmountLabel ? ` (${paymentLinkAmountLabel})` : null}
                        </Checkbox>
                    </Space>
                </Tooltip>
            );
        };

        // Prüfen ob dies ein Buchungsbestätigungs-Template ist (BookingConfirmation oder WaitingListConfirmation)
        const isBookingConfirmationTemplate = templateType === TemplateType.BookingConfirmation || templateType === TemplateType.WaitingListConfirmation;

        // Für Buchungsbestätigungen: Neuen Button "Senden ohne zu speichern" anzeigen wenn bearbeitet
        const handleSendWithoutSaving = () => {
            shouldSendMail(true, true); // true = senden, true = mit bearbeiteten Texten
        };

        const handleSendNormal = () => {
            if (isBookingConfirmationTemplate) {
                // Für Buchungsbestätigung: Direkt senden ohne Template zu speichern
                shouldSendMail(true, false);
            } else {
                // Für andere Templates: Bisheriges Verhalten (speichern + senden via sendMail)
                sendMail();
            }
        };

        const handleSaveAndSend = async () => {
            if (isBookingConfirmationTemplate) {
                // Erst Template speichern, dann senden
                try {
                    await updateTemplate();
                    shouldSendMail(true, false); // Nach Speichern ohne editedTemplateInput senden (DB-Werte werden verwendet)
                } catch (error) {
                    console.log(`Error on updating Template before send: ${error}`);
                }
            } else {
                // Bisheriges Verhalten für andere Templates
                sendMail();
            }
        };

        // Reihe 1 (Optionen) nur rendern, wenn mindestens ein Optionen-Element sichtbar wird.
        const showOptionsRow = showInvoiceAttachments || showPaymentLinkCheckbox;

        return (
            <div
                style={{
                    width: "100%",
                    display: "flex",
                    flexDirection: "column",
                    gap: 12,
                }}
            >
                {showOptionsRow && (
                    <div
                        style={{
                            display: "flex",
                            flexWrap: "wrap",
                            alignItems: "center",
                            gap: 12,
                        }}
                    >
                        <InvoiceCheckbox />
                        <PaymentLinkCheckbox />
                    </div>
                )}
                <Space
                    style={{
                        display: "flex",
                        flexWrap: "wrap",
                        justifyContent: "flex-end",
                        alignItems: "center",
                        rowGap: 8,
                    }}
                >
                    {receivers.length === 1 && (
                        <Button icon={<FilePdfOutlined />} onClick={handleLetterPdfClick} loading={renderLetterPdfLoading}>
                            {"Als Brief"}
                        </Button>
                    )}
                    {edit && (
                        <Button key="cancel-edit" onClick={() => setEdit(false)} danger>
                            {"Abbrechen"}
                        </Button>
                    )}
                    {!edit && (
                        <Button key="edit" icon={<EditOutlined />} onClick={() => setEdit(true)}>
                            {"Vorlage bearbeiten"}
                        </Button>
                    )}
                    {!hideNotSendButton &&
                        (showPaymentLinkCheckbox && sendPaymentLinkEnabled ? (
                            <Tooltip title={"Bezieht sich nur auf die Buchungsbestätigung. Der Zahlungslink wird trotzdem versendet, solange die Checkbox oben aktiv ist."}>
                                <Button key="doNotSend" onClick={() => shouldSendMail(false)} danger ghost>
                                    {"Bestätigung nicht senden"}
                                </Button>
                            </Tooltip>
                        ) : (
                            <Button key="doNotSend" onClick={() => shouldSendMail(false)} danger ghost>
                                {"Nicht senden"}
                            </Button>
                        ))}

                    {/* Button "Senden ohne zu speichern" - für bearbeitete Vorlagen beim nachträglichen Versenden oder bei Buchungsbestätigungen */}
                    {(hideNotSendButton || isBookingConfirmationTemplate) && templateHasBeenEdited && (
                        <Tooltip title="Sendet die Email mit den bearbeiteten Texten, ohne die Vorlage dauerhaft zu speichern.">
                            <Button key="sendWithoutSaving" onClick={handleSendWithoutSaving} loading={sendTemplateMailLoading || updateLoading} disabled={noReceiverFound}>
                                {"Senden ohne zu speichern"}
                            </Button>
                        </Tooltip>
                    )}

                    <Tooltip
                        color={red[3]}
                        title={
                            noReceiverFound
                                ? "Emailadresse des Empfängers fehlt."
                                : noTemplateLoaded
                                ? "Es ist keine Vorlage für diesen E-Mail-Typ hinterlegt. Bitte unter Einstellungen → Vorlagen eine Vorlage anlegen, bevor du E-Mails versendest."
                                : null
                        }
                    >
                        <Button
                            key="submit"
                            onClick={templateHasBeenEdited && templateType !== TemplateType.NoTemplate ? handleSaveAndSend : handleSendNormal}
                            loading={sendTemplateMailLoading || updateLoading}
                            type="primary"
                            disabled={noReceiverFound || noTemplateLoaded}
                        >
                            {(() => {
                                // Wenn nichts bearbeitet wurde oder NoTemplate: Einfach "E-Mail senden"
                                if (!templateHasBeenEdited || templateType === TemplateType.NoTemplate) {
                                    // Spezifischer Text je nach Template-Typ
                                    if (templateType === TemplateType.BookingConfirmation) {
                                        return "Buchungsbestätigung senden";
                                    } else if (templateType === TemplateType.WaitingListConfirmation) {
                                        return "Wartelistenbestätigung senden";
                                    } else if (templateType === TemplateType.Invoice || templateType === TemplateType.InvoiceMonthly || templateType === TemplateType.InvoiceMonthlyConsistent) {
                                        return "Rechnung senden";
                                    } else if (
                                        templateType === TemplateType.InvoiceReversal ||
                                        templateType === TemplateType.InvoiceReversalMonthly ||
                                        templateType === TemplateType.InvoiceReversalMonthlyConsistent
                                    ) {
                                        return "Stornorechnung senden";
                                    }
                                    return i18n.containers.templates.EmailTemplates.modalButtons.send();
                                }
                                // Wenn bearbeitet: "Vorlage speichern und E-Mail senden"
                                return i18n.containers.templates.EmailTemplates.modalButtons.saveAndSend();
                            })()}
                        </Button>
                    </Tooltip>
                </Space>
            </div>
        );
    };

    // Variables --------------------------------------------------------------------------------------------------------------------------------

    const variables = {
        subject: templateContent?.subject,
        headerText: templateContent?.headerText,
        footerText: templateContent?.footerText,
        signature: templateContent?.signature,
        course: {
            courseNumber: "TEST-100079",
            name: "Testkurs",
            subTitle: "Spielend Schwimmen lernen",
        },
    };

    // Vorschau mit ersetzten Variablen (Plan 2026-09-02 § 3.2): rendert den Entwurf serverseitig für den
    // angezeigten Empfänger; das Ergebnis fließt NIE in templateContent zurück (Lehre aus 01/2024).
    const previewIds = bookingId ? [bookingId] : emailData?.ids ?? [];
    const textBlockPreview = useTextBlockPreview({
        enabled: !edit && open !== false,
        templateId: dbTemplateId ?? actualDbTemplateId,
        mailDataType: emailData?.mailDataType ?? mailDataType,
        ids: previewIds,
        draft: templateContent,
        // Vorlagen-Seite (Einstellungen → Vorlagen): keine Empfänger, dafür Musterdaten (Plan Q18 b).
        sampleData: true,
    });

    // send mail --------------------------------------------------------------------------------------------------------------------------------

    const [sendTemplateMail, {data: sendTemplateMailData, error: sendTemplateMailError, loading: sendTemplateMailLoading}] = useSendEmailTemplateMutation();

    const sendMail = async () => {
        console.group("sendMail");
        // antMessage.loading({
        //     key: "sendMail",
        //     content: "E-Mails werden versendet...",
        //     duration: 0,
        // });
        if (templateContent.templateType !== TemplateType.NoTemplate) {
            try {
                await updateTemplate();
            } catch (error) {
                console.log(`Error on updating Template ${error}`);
            }
        }
        const {templateType, footerText, headerText, signature, subject, name} = templateContent;

        const templateName = name ?? "";

        const message: SendParamsMessageInput = {
            From: {Email: "", Name: ""},
            Sender: {Email: "", Name: ""},
            To: [{Email: "", Name: ""}],
            Subject: "",
            Variables: {
                subject: subject ?? "",
                headerText: headerText ?? "",
                footerText: footerText ?? "",
                signature: signature ?? "",
                replyTo: "replyTo",
                bookerId: "bookerId",
                customId: uuid.v4(),
            },
        };

        const typedTemplateType = templateType as TemplateType;

        const typedMailjetTemplateId = templateType === "FreeText" || templateType === "NoTemplate" ? MailjetEmailTemplates.FreeText : MailjetEmailTemplates.BookingConfirmation;

        if (emailData) {
            const sendTemplateInput: SendTemplateInput = {
                templateType: typedTemplateType,
                templateName,
                mailjetTemplateId: typedMailjetTemplateId,
                messages: [message],
                emailData: {
                    receivers: emailData.receivers,
                    ids: emailData.ids,
                    mailDataType: emailData.mailDataType,
                    attachInvoices: attachInvoices || undefined,
                    attachReversalInvoices: attachReversalInvoices || undefined,
                },
            };

            try {
                antMessage.loading({
                    key: "sendMail",
                    content: "E-Mails werden versendet...",
                    duration: 0,
                });

                await sendTemplateMail({
                    variables: {
                        sendTemplateInput,
                    },
                }).then((res) => {
                    // console.log("sendTemplateMail res::: ", res);
                    const sentMailResult: SentMailsResult = res.data?.sendEmailTemplate;

                    if (sentMailResult) {
                        const mailCount = sentMailResult.sentMailCount;
                        const emailText = mailCount === 1 ? "E-Mail" : "E-Mails";
                        const receivers = sentMailResult.sentMessages.map((message) => message.To[0].Email);
                        const receiverString = receivers.join(", ");

                        antMessage.success({
                            key: "sendMail",
                            content: `${mailCount} ${emailText} erfolgreich versendet. Empfänger: ${receiverString}`,
                        });
                    }
                });

                if (confirmAction) {
                    // Für den generischen sendMail Pfad: ohne editedTemplateInput
                    // (Template wurde bereits vorher gespeichert)
                    confirmAction(true, undefined);
                }
            } catch (error) {
                let errorText = "Fehler beim E-Mailversand!";

                if (error instanceof Error) {
                    errorText = error.message;

                    if (error.message.includes("Only canceled bookings")) {
                        errorText = "Es wurden keine E-Mails versendet, da nur Buchungen die nicht storniert wurden ausgewählt werden können.";
                    } else if (error.message.includes("subjectToLong")) {
                        errorText = "Text des Betreffs ist zu lang. Es sind maximal 255 Zeichen erlaubt. Bitte korrigiere den Betreff um den E-Mailversand ausführen zu können.";
                    } else if (error.message.includes("no email addresses for receivers") || error.message.includes("No email addresses")) {
                        errorText = "Mindestens Empfänger hat keine E-Mail Adresse hinterlegt.";
                    } else if (error.message.includes("Total number of recipients exceeded")) {
                        errorText = "Zu viele Empfänger. Es können derzeit maximal 25 E-Mails auf einmal versendet werden.";
                    } else {
                        errorText = "Unbekannter Fehler beim Senden der Email";
                        console.log("error: ", error);
                    }
                } else {
                    console.log("error: ", error);
                }

                antMessage.error({
                    content: "Fehler beim E-Mailversand!",
                });

                Modal.error({
                    icon: null,
                    title: "Fehler beim E-Mailversand!",
                    content: errorText,
                    cancelText: null,
                    style: {textAlign: "center"},
                    onOk: () => {
                        if (confirmAction) {
                            console.log("confirmAction");
                            confirmAction(false);
                        }
                    },
                    okButtonProps: {block: true},
                });
            } finally {
                antMessage.destroy("sendMail");
            }
        } else {
            shouldSendMail(true);
            console.error("emailData is undefined");
        }
        console.log("sendTemplateMailLoading: ", sendTemplateMailLoading);
        console.log("email has been sent");
        // /////////////////////////////////////////////////////
        console.groupEnd();
    };

    // return --------------------------------------------------------------------------------------------------------------------------------

    const renderContent = () => {
        return (
            <div style={style} data-testid="mail-template-editor-content">
                <Spin spinning={dbLoadingTemplateById || dbLoadingTemplateByType || mjmlTemplateLoading || updateLoading || deleting || sendTemplateMailLoading}>
                    <ErrorView messages={[dbErrorTemplateById, dbErrorTemplateByType, mjmlTemplateError, createUpdateError]} />
                    {templateContent && mjmlTemplate && (
                        <>
                            {edit ? (
                                <>
                                    {nameInputField}
                                    {textFields.map((field) => {
                                        return (
                                            <TemplateInputField
                                                minRows={field === "subject" ? 1 : field === "signature" ? 2 : 5}
                                                key={field}
                                                field={field as any}
                                                templateContent={templateContent}
                                                handleTextChange={(description, content) => handleTextChange(description, content)}
                                                mailDataType={emailData?.mailDataType ?? mailDataType ?? MailDataType.Booker}
                                                featureLessonBooking={featureLessonBooking}
                                                className={css.textArea}
                                            />
                                        );
                                    })}
                                </>
                            ) : (
                                <>
                                    <ResolvedPreviewBar preview={textBlockPreview} />
                                    <MjmlLoader
                                        data={data}
                                        bookingId={bookingId}
                                        variables={variables}
                                        //   courseLessons={courseLessons}
                                        mjmlTemplate={mjmlTemplate.Data}
                                        loading={mjmlTemplateLoading}
                                        bookingData={
                                            data?.attendeeId && data?.courseId
                                                ? {
                                                      attendeeId: data.attendeeId,
                                                      courseId: data.courseId,
                                                  }
                                                : undefined
                                        }
                                        dbTemplateId={dbTemplateId ?? actualDbTemplateId ?? ""}
                                        renderWithoutCourseDetails={renderWithoutCourseDetails}
                                        resolvedTextBlocks={textBlockPreview.resolvedTextBlocks}
                                        showVariableNames={textBlockPreview.showVariableNames}
                                    />
                                </>
                            )}
                        </>
                    )}
                </Spin>
            </div>
        );
    };

    // RECEIVERBAR ------------------------------------------------------------------------------------------------------

    const ReceiversBar = () => {
        // IF TYPE IS COURSE WE HAVE TO FETCH THE MAILADDRESSE ----------------------------------------------------------
        const getCourseIds = () => {
            if (emailData?.mailDataType === MailDataType.Course) {
                return emailData.ids;
            }

            return undefined;
        };

        const courseIds = getCourseIds();

        // console.log("courseIds: ", courseIds);

        const {data: receiversOfCourseData} = useGetReceiversByCourseIdsQuery({
            skip: !courseIds || courseIds.length === 0,
            variables: {courseIds: courseIds ?? []},
        });

        const receiversOfCourse: GetReceiversByCourseIdsQuery["getBookersByCourseIds"] | undefined = receiversOfCourseData?.getBookersByCourseIds;

        const receiversOfCourseEmails = receiversOfCourse
            ?.map((booker) => {
                return booker.email;
            })
            .filter((email) => email !== null && email !== undefined && email !== "");

        const emailCount = receiversOfCourseEmails?.length;

        setEmailToSendCount(emailCount);

        const isDemoAccount = subscriptionType === SubscriptionType.TestSubscription;

        const {data: ownerMailData} = useGetOwnerEmailQuery();

        const ownerMail: GetOwnerEmailQuery["ownerEmail"] | string = ownerMailData?.ownerEmail.ownerEmail ?? "";

        const checkedReceivers = () => {
            if (emailData?.mailDataType === MailDataType.Course && receiversOfCourseEmails) {
                if (isDemoAccount) {
                    return receiversOfCourseEmails.map((receiver) => {
                        return `${ownerMail}*`;
                    });
                }

                return receiversOfCourseEmails;
            }

            if (isDemoAccount) {
                return receivers.map((receiver) => {
                    return `${ownerMail}*`;
                });
            }

            if (mailDataType === MailDataType.Course) {
                return receiversOfCourseEmails;
            }

            return receivers;
        };

        // return --------------------------------------------------------------------------------------------------------------------------------

        return (
            <Space size="small" className={css.receiversBar} wrap>
                {isDemoAccount && (
                    <Tooltip title="Da es sich um einen DemoAccount handelt, wird hier die E-MailAdresse die du in den Settings hinterlegt hast angezeigt.">
                        <InfoCircleOutlined style={{marginRight: "3px"}} />
                    </Tooltip>
                )}

                <div>{"Empfänger:"}</div>
                {checkedReceivers()?.map((receiver, index) => {
                    return (
                        <Tag key={`${receiver}-${index}`} className={css.receiverTag}>
                            {receiver}
                        </Tag>
                    );
                })}
            </Space>
        );
    };

    const letterPdfModal = (
        <LetterPdfPreviewModal
            open={letterPdfModalVisible}
            onClose={() => setLetterPdfModalVisible(false)}
            letterData={letterData}
            fileName={`Brief_${letterData?.recipientLastName || "Empfaenger"}`}
        />
    );

    if (asModal === true) {
        return (
            <>
                <DraggableModal
                    data-testid="mail-template-editor"
                    draggable={true}
                    boundsPadding={500}
                    title={headerSection(templateContent, useCase)}
                    open={open}
                    // closable={false}
                    // Kein eigener onClick am X: antd ruft beim Klick ohnehin onCancel. Mit beiden Handlern lief
                    // dismissWithoutDiscardingSend zweimal, Rechnung oder Zahlungslink gingen doppelt raus.
                    closeIcon={
                        <Button size="large" type="text" style={{marginTop: 15}}>
                            <CloseOutlined />
                        </Button>
                    }
                    onCancel={() =>
                        dismissWithoutDiscardingSend(() => {
                            handleCancel();
                            confirmAction?.(false);
                        })
                    }
                    destroyOnClose={true}
                    bodyStyle={{
                        overflow: edit ? "auto" : "hidden",
                        maxHeight: "calc(100vh - 200px)",
                        padding: "0px",
                    }}
                    style={{top: 40}}
                    // width={860}
                    width={800}
                    footer={<Footer useCase={useCase} />}
                >
                    <>
                        <ReceiversBar />
                        {noTemplateLoaded && (
                            <Alert
                                type="warning"
                                showIcon
                                style={{margin: "10px 16px"}}
                                message="Keine Vorlage hinterlegt"
                                description={
                                    "Für diesen E-Mail-Typ ist im Tenant noch keine Vorlage angelegt. Bitte unter Einstellungen → Vorlagen eine passende Vorlage erstellen. Der „Senden“-Button ist solange deaktiviert."
                                }
                            />
                        )}
                        {renderContent()}
                    </>
                </DraggableModal>
                {letterPdfModal}
            </>
        );
    }

    return (
        <>
            {renderContent()}
            {letterPdfModal}
        </>
    );
};

export default memo(MailTemplateEditor);
